// Badge catalog. Conditions evaluated against gamificationStats object.
// Adding a badge here is sufficient — no DB schema change needed.
//
// Extra metadata drives the Profile "collection" UI:
//   category  → grouping into vertical sections / carousels
//   rarity    → styling (comune | raro | epico | leggendario)
//   secret    → hide name/description while locked (shows "???")
//   target + getProgress → progress bar for locked badges
// NB: badge `key` values are persisted in DB (athlete_badges) — never rename them.

// Category order + labels drive the Profile UI sections.
export const BADGE_CATEGORIES = [
  { key: 'workouts', label: 'Allenamenti' },
  { key: 'consistency', label: 'Costanza' },
  { key: 'extra', label: 'Serie Extra' },
  { key: 'xp', label: 'Livello & XP' },
  { key: 'secret', label: 'Badge Segreti' },
];

// Short flavor text shown as the badge "description" in the detail sheet.
export const CATEGORY_FLAVOR = {
  workouts: 'Ricompensa la costanza in palestra.',
  consistency: 'Premia chi non salta gli allenamenti.',
  extra: 'Per chi dà sempre qualcosa in più.',
  xp: 'Traguardi di livello ed esperienza.',
  secret: 'Un traguardo speciale tutto da scoprire.',
};

// Rarity metadata for styling in cards and detail sheet (Tailwind class literals
// live here so JIT keeps them — src/** is scanned).
export const RARITY = {
  comune:      { label: 'Comune',      order: 0, chip: 'bg-gray-100 text-gray-600',       ring: 'ring-gray-200',   glow: 'from-gray-100 to-gray-50' },
  raro:        { label: 'Raro',        order: 1, chip: 'bg-sky-100 text-sky-700',         ring: 'ring-sky-300',    glow: 'from-sky-100 to-white' },
  epico:       { label: 'Epico',       order: 2, chip: 'bg-violet-100 text-violet-700',   ring: 'ring-violet-300', glow: 'from-violet-100 to-white' },
  leggendario: { label: 'Leggendario', order: 3, chip: 'bg-amber-100 text-amber-700',     ring: 'ring-amber-300',  glow: 'from-amber-100 to-white' },
};

export const BADGES = [
  // ── Allenamenti ──────────────────────────────────────────────
  {
    key: 'first_step', name: 'Primo Passo', description: 'Completa il tuo primo allenamento', icon: '👟',
    category: 'workouts', rarity: 'comune', target: 1, getProgress: (s) => s.totalWorkouts,
    condition: (s) => s.totalWorkouts >= 1,
  },
  {
    key: 'consistency_5', name: 'Costante', description: 'Completa 5 allenamenti', icon: '🔥',
    category: 'workouts', rarity: 'comune', target: 5, getProgress: (s) => s.totalWorkouts,
    condition: (s) => s.totalWorkouts >= 5,
  },
  {
    key: 'dedicated_10', name: 'Dedicato', description: 'Completa 10 allenamenti', icon: '💪',
    category: 'workouts', rarity: 'raro', target: 10, getProgress: (s) => s.totalWorkouts,
    condition: (s) => s.totalWorkouts >= 10,
  },
  {
    key: 'veteran_25', name: 'Veterano', description: 'Completa 25 allenamenti', icon: '⚔️',
    category: 'workouts', rarity: 'raro', target: 25, getProgress: (s) => s.totalWorkouts,
    condition: (s) => s.totalWorkouts >= 25,
  },
  {
    key: 'centurion_50', name: 'Centurione', description: 'Completa 50 allenamenti', icon: '🛡️',
    category: 'workouts', rarity: 'epico', target: 50, getProgress: (s) => s.totalWorkouts,
    condition: (s) => s.totalWorkouts >= 50,
  },
  {
    key: 'legend_100', name: 'Leggenda', description: 'Completa 100 allenamenti', icon: '👑',
    category: 'workouts', rarity: 'leggendario', target: 100, getProgress: (s) => s.totalWorkouts,
    condition: (s) => s.totalWorkouts >= 100,
  },

  // ── Costanza ─────────────────────────────────────────────────
  {
    key: 'free_spirit', name: 'Spirito Libero', description: 'Completa il tuo primo allenamento libero', icon: '🕊️',
    category: 'consistency', rarity: 'comune', target: 1, getProgress: (s) => s.freeWorkouts,
    condition: (s) => s.freeWorkouts >= 1,
  },
  {
    key: 'free_regular', name: 'Anima Libera', description: 'Completa 10 allenamenti liberi', icon: '🌿',
    category: 'consistency', rarity: 'raro', target: 10, getProgress: (s) => s.freeWorkouts,
    condition: (s) => s.freeWorkouts >= 10,
  },

  // ── Serie Extra ──────────────────────────────────────────────
  {
    key: 'extra_10', name: 'Extra Mile', description: 'Completa 10 serie extra', icon: '➕',
    category: 'extra', rarity: 'comune', target: 10, getProgress: (s) => s.extraSets,
    condition: (s) => s.extraSets >= 10,
  },
  {
    key: 'extra_50', name: 'Oltre i Limiti', description: 'Completa 50 serie extra', icon: '🚀',
    category: 'extra', rarity: 'epico', target: 50, getProgress: (s) => s.extraSets,
    condition: (s) => s.extraSets >= 50,
  },

  // ── Livello & XP ─────────────────────────────────────────────
  {
    key: 'level_5', name: 'Guerriero', description: 'Raggiungi il livello 5', icon: '⚡',
    category: 'xp', rarity: 'raro', target: 5, getProgress: (s) => s.currentLevel,
    condition: (s) => s.currentLevel >= 5,
  },
  {
    key: 'level_10', name: 'Campione', description: 'Raggiungi il livello 10', icon: '🏆',
    category: 'xp', rarity: 'epico', target: 10, getProgress: (s) => s.currentLevel,
    condition: (s) => s.currentLevel >= 10,
  },
  {
    key: 'xp_500', name: 'Atleta XP', description: 'Raggiungi 500 XP totali', icon: '⭐',
    category: 'xp', rarity: 'raro', target: 500, getProgress: (s) => s.totalXp,
    condition: (s) => s.totalXp >= 500,
  },

  // ── Badge Segreti ────────────────────────────────────────────
  {
    key: 'hidden_grinder', name: 'Instancabile', description: 'Completa 100 serie extra', icon: '🥷',
    category: 'secret', rarity: 'epico', secret: true, target: 100, getProgress: (s) => s.extraSets,
    condition: (s) => s.extraSets >= 100,
  },
  {
    key: 'hidden_devotion', name: 'Devozione', description: 'Completa 75 allenamenti', icon: '🔮',
    category: 'secret', rarity: 'leggendario', secret: true, target: 75, getProgress: (s) => s.totalWorkouts,
    condition: (s) => s.totalWorkouts >= 75,
  },
];

export const getBadgeByKey = (key) => BADGES.find((b) => b.key === key) || null;

export const getBadgesByCategory = (category) => BADGES.filter((b) => b.category === category);

/** Returns badges to award (condition met, not yet earned) */
export const evaluateNewBadges = (stats, earnedKeys) => {
  const earned = new Set(earnedKeys);
  return BADGES.filter((b) => !earned.has(b.key) && b.condition(stats));
};
